"use client";

import { c as _c } from "react/compiler-runtime";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { useEffect, useRef, useState } from "react";
import { getDesiredLevel, mapFlyTo, processNodes } from "../mapUtils";
import { OutputNode, OutputNodeAtom } from "@/types/real-estate";
import { FLYTO_OFFSET_CLUSTER, FLYTO_OFFSET_CLUSTER_MOBILE, FLYTO_OFFSET_SINGLE, FLYTO_OFFSET_SINGLE_FULLMAP } from "../../map.constants";
import useMapZoom from "@/components/organisms/map/hooks/useMapZoom";
import useMapPan from "@/components/organisms/map/hooks/useMapPan";
import { useView } from "../../../providers/ViewProvider";
import { useStableSearchParams } from "@/hooks/customSearchParams/useCustom";
import { useMobile } from "@/components/providers/MobileProvider";
import { useMapContext } from "@/components/organisms/map/components/MapProvider";
export function findNodeAtom(nodes: (OutputNode | OutputNodeAtom)[], realEstateId: string): OutputNodeAtom | null {
  for (const node of nodes) {
    if ("realEstateId" in node && node.realEstateId === realEstateId) {
      return node as OutputNodeAtom;
    }
    if ("children" in node && (node as OutputNode).children) {
      const found = findNodeAtom((node as OutputNode).children, realEstateId);
      if (found) return found;
    }
  }
  return null;
}
export function isClusterTarget(nodes: (OutputNode | OutputNodeAtom)[], lat: number, lng: number): boolean {
  const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
  let count = 0;
  const stack = [...nodes];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if ("realEstateId" in node) {
      const atom = node as OutputNodeAtom;
      if (atom.lat && atom.lng && `${atom.lat.toFixed(6)},${atom.lng.toFixed(6)}` === key) {
        count++;
        if (count > 1) return true;
      }
    } else if ("children" in node && (node as OutputNode).children) {
      stack.push(...(node as OutputNode).children);
    }
  }
  return false;
}
type UseMarkersProps = {
  realEstateNodes: OutputNode[];
  callbackTrigger: (realEstateId: string) => void;
  isLoading?: boolean;
};
export function useMarkers(t0) {
  const $ = _c(36);
  const {
    realEstateNodes,
    callbackTrigger,
    isLoading
  } = t0;
  const isMobile = useMobile();
  const map = useMap();
  const {
    zoom
  } = useMapZoom();
  const {
    forcedMapTarget,
    setForcedMapTarget,
    mapRef
  } = useMapContext();
  let t1;
  let t2;
  if ($[0] !== map || $[1] !== mapRef) {
    t1 = () => {
      mapRef.current = map;
      return () => {
        mapRef.current = null;
      };
    };
    t2 = [map, mapRef];
    $[0] = map;
    $[1] = mapRef;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const {
    showMap,
    mapSize
  } = useView();
  const fullmap = mapSize === "max-size" && showMap;
  useMapPan();
  const searchParams = useStableSearchParams();
  let t3;
  if ($[4] !== searchParams) {
    const p = new URLSearchParams(searchParams.toString());
    p.delete("modal");
    t3 = p.toString();
    $[4] = searchParams;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const filterKey = t3;
  const [overrideZoom, setOverrideZoom] = useState(null);
  const fitBoundsCounterRef = useRef(0);
  const desiredLevel = getDesiredLevel(overrideZoom ?? zoom);
  let t4;
  if ($[6] !== overrideZoom) {
    t4 = () => {
      if (overrideZoom !== null) {
        setOverrideZoom(null);
      }
    };
    $[6] = overrideZoom;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== zoom) {
    t5 = [zoom];
    $[8] = zoom;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] !== setForcedMapTarget) {
    t6 = realEstateId => setForcedMapTarget({
      realEstateId
    });
    $[10] = setForcedMapTarget;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    markers,
    coords
  } = processNodes(realEstateNodes, desiredLevel, map, fullmap, isMobile, callbackTrigger, "", t6);
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      fitBoundsCounterRef.current = 0;
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== filterKey) {
    t8 = [filterKey];
    $[13] = filterKey;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  if ($[15] !== forcedMapTarget || $[16] !== fullmap || $[17] !== isLoading || $[18] !== isMobile || $[19] !== map || $[20] !== realEstateNodes) {
    t9 = () => {
      if (!forcedMapTarget || isLoading) {
        return;
      }
      const atom = findNodeAtom(realEstateNodes, forcedMapTarget.realEstateId);
      if (atom?.isDemand) {
        return;
      }
      const lat = atom?.lat ?? forcedMapTarget.fallbackCoords?.[0];
      const lng = atom?.lng ?? forcedMapTarget.fallbackCoords?.[1];
      if (!lat || !lng) {
        return;
      }
      const cluster = isClusterTarget(realEstateNodes, lat, lng);
      const offsetY = cluster ? isMobile ? FLYTO_OFFSET_CLUSTER_MOBILE : FLYTO_OFFSET_CLUSTER : fullmap && !isMobile ? FLYTO_OFFSET_SINGLE_FULLMAP : FLYTO_OFFSET_SINGLE;
      map.eachLayer(_temp);
      setTimeout(() => mapFlyTo({
        map,
        lat,
        lng,
        offsetY,
        onAnimationComplete: () => {
          setOverrideZoom(map.getZoom());
          const target = L.latLng(lat, lng);
          const tryOpenClosest = () => {
            let closest = null;
            let minDist = Infinity;
            map.eachLayer(layer_0 => {
              if (layer_0 instanceof L.Marker) {
                const dist = layer_0.getLatLng().distanceTo(target);
                if (dist < minDist) {
                  minDist = dist;
                  closest = layer_0;
                }
              }
            });
            if (closest && minDist < 1000) {
              (closest as L.Marker).openPopup();
              return true;
            }
            return false;
          };
          const onLayerAdd = e => {
            if (e.layer instanceof L.Marker) {
              const dist_0 = e.layer.getLatLng().distanceTo(target);
              if (dist_0 < 1000) {
                e.layer.openPopup();
                map.off("layeradd", onLayerAdd);
              }
            }
          };
          map.on("layeradd", onLayerAdd);
          setTimeout(() => {
            if (tryOpenClosest()) {
              map.off("layeradd", onLayerAdd);
            }
          }, 250);
          setTimeout(() => map.off("layeradd", onLayerAdd), 3000);
        }
      }), 0);
    };
    $[15] = forcedMapTarget;
    $[16] = fullmap;
    $[17] = isLoading;
    $[18] = isMobile;
    $[19] = map;
    $[20] = realEstateNodes;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== forcedMapTarget || $[23] !== isLoading || $[24] !== realEstateNodes) {
    t10 = [forcedMapTarget, isLoading, realEstateNodes];
    $[22] = forcedMapTarget;
    $[23] = isLoading;
    $[24] = realEstateNodes;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  useEffect(t9, t10);
  let t11;
  if ($[26] !== coords || $[27] !== forcedMapTarget || $[28] !== map) {
    t11 = () => {
      if (coords.length > 0 && fitBoundsCounterRef.current < 2 && !forcedMapTarget) {
        if (coords.length > 2) {
          const latSum = coords.reduce(_temp2, 0);
          const lngSum = coords.reduce(_temp3, 0);
          const centroid = [latSum / coords.length, lngSum / coords.length];
          const distances = coords.map(c_1 => ({
            coord: c_1,
            dist: L.latLng(c_1[0], c_1[1]).distanceTo(L.latLng(centroid[0], centroid[1]))
          }));
          const keepCount = Math.max(2, Math.floor(coords.length * 0.95));
          const filtered = distances.sort(_temp4).slice(0, keepCount).map(_temp5);
          map.fitBounds(filtered, {
            padding: [30, 30]
          });
        } else {
          map.fitBounds(coords, {
            padding: [30, 30]
          });
        }
        if (map.getZoom() > 11) {
          map.setZoom(11);
        }
        fitBoundsCounterRef.current = fitBoundsCounterRef.current + 1;
      }
    };
    $[26] = coords;
    $[27] = forcedMapTarget;
    $[28] = map;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== coords || $[31] !== filterKey || $[32] !== map) {
    t12 = [map, filterKey, coords];
    $[30] = coords;
    $[31] = filterKey;
    $[32] = map;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  useEffect(t11, t12);
  let t13;
  if ($[34] !== markers) {
    t13 = {
      markers
    };
    $[34] = markers;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  return t13;
}
function _temp5(d) {
  return d.coord;
}
function _temp4(a, b) {
  return a.dist - b.dist;
}
function _temp3(sum_0, c_0) {
  return sum_0 + c_0[1];
}
function _temp2(sum, c) {
  return sum + c[0];
}
function _temp(layer) {
  if (layer instanceof L.Marker) {
    layer.closePopup();
  }
}